import { useOrdersConfig } from '../config';
import { ALL, FILTER_KEYS } from '../consts';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DgcOrdersUrlParams } from 'shared/types/dgcOrders';
import { isDefined } from 'shared/utils/bids';
import { normalizeDgcPageNum } from 'shared/utils/dgcOrders';

// pageNum is normalized separately, and pageSize/cardType are set programmatically rather than read from the URL.
type UrlFilterKey = Exclude<keyof DgcOrdersUrlParams, 'pageNum' | 'pageSize' | 'cardType'>;

function useFilters() {
  const [searchParams] = useSearchParams();
  const { cardType } = useOrdersConfig();

  const queryParams = useMemo<DgcOrdersUrlParams>(() => {
    const resultParams: DgcOrdersUrlParams = {
      pageNum: String(normalizeDgcPageNum(searchParams.get(FILTER_KEYS.pageNum))),
      cardType,
    };

    for (const filterName of Object.values(FILTER_KEYS).filter((key) => key !== FILTER_KEYS.pageNum)) {
      const filterQueryParam = searchParams.get(filterName);

      if (filterQueryParam) {
        resultParams[filterName as UrlFilterKey] = filterQueryParam;
      } else if (filterName === FILTER_KEYS.status) {
        resultParams[filterName as UrlFilterKey] = ALL;
      }
    }

    return resultParams;
  }, [searchParams, cardType]);

  return queryParams;
}

// True when the user has narrowed the list, so an empty result can be explained as "no matches" rather than "no data".
export function areFiltersModified(params: DgcOrdersUrlParams): boolean {
  return (
    params.status !== ALL ||
    isDefined(params.deliveryMethod) ||
    isDefined(params.paymentType) ||
    isDefined(params.clientName) ||
    isDefined(params.orderRef) ||
    isDefined(params.createdDateFrom) ||
    isDefined(params.createdDateTo)
  );
}

export default useFilters;
